import { useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CardMedia from '@mui/material/CardMedia';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import fallbackImage from '@/assets/images/consultation.png';
import { ExternalLink } from '@/components/ExternalLink';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ADVISOR_DID_YOU_KNOW_TIPS } from '@/constants/advisorDidYouKnow';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import { StandardWidgetProps } from '@/models/WidgetModels';
import {
  AdvisorQuickLink,
  useGetAdvisorQuickLinksQuery,
} from '@/store/quickLinksResourcesApi';
import { ContentContainer } from '@/widgets/contentContainer';

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

function QuickLinkCard({ link }: { link: AdvisorQuickLink }) {
  const imageSrc = link.Photo || (!isEmbeddableBundle ? fallbackImage : '');

  return (
    <ExternalLink
      url={link.Url}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      {imageSrc && (
        <CardMedia
          component='img'
          image={imageSrc}
          alt={link.Name}
          onError={(e) => {
            e.currentTarget.onerror = null;
            if (isEmbeddableBundle) {
              e.currentTarget.removeAttribute('src');
              return;
            }
            e.currentTarget.src = fallbackImage;
          }}
          sx={{ maxHeight: 150, objectFit: 'contain' }}
        />
      )}
      <Box>
        <Typography
          component='div'
          variant='subtitle1'
        >
          {link.Name}
        </Typography>
        {link.Description && (
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {link.Description}
          </Typography>
        )}
      </Box>
    </ExternalLink>
  );
}

export default function AdvisorQuickLinksWidget({
  title,
  isHalfWidth,
  onDataLoaded,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('widgetQuickLinks');

  const authLoaded = useAuthIsLoaded();
  const { data, isLoading, isUninitialized, error } =
    useGetAdvisorQuickLinksQuery(authLoaded ? undefined : skipToken);
  const isPending = !authLoaded || isUninitialized || isLoading;

  useEffect(() => {
    if (data || error) {
      onDataLoaded?.();
    }
  }, [data, error, onDataLoaded]);

  const links = data?.Records ?? [];
  const tips = ADVISOR_DID_YOU_KNOW_TIPS;
  const hasNoContent = !isPending && !error && !links.length && !tips.length;

  const renderQuickLinks = () => {
    if (isPending) {
      return <LoadingSpinner />;
    }
    if (error) {
      return (
        <Alert severity='error'>{t('advisorQuickLinks.errorLoading')}</Alert>
      );
    }
    if (!links.length) {
      return (
        <Typography color='text.secondary'>
          {t('advisorQuickLinks.noQuickLinks')}
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 3,
        }}
      >
        {links.map((link) => (
          <QuickLinkCard
            key={link.UserExternalLinkId}
            link={link}
          />
        ))}
      </Box>
    );
  };

  return (
    <ContentContainer
      container={container}
      sx={{ display: 'flex', flexDirection: 'column', height: 'fit-content' }}
    >
      <Typography
        variant='h6'
        sx={{ fontWeight: 600 }}
      >
        {widgetTitle}
      </Typography>
      <Divider sx={{ my: 2 }} />

      {hasNoContent ? (
        <Typography color='text.secondary'>
          {t('advisorQuickLinks.noContent')}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isHalfWidth
              ? '1fr'
              : { xs: '1fr', md: '2fr 1fr' },
            gap: 4,
          }}
        >
          <Box
            component='section'
            aria-label={t('quickLinks')}
            aria-busy={isPending}
          >
            <Typography
              variant='subtitle2'
              sx={{ mb: 2 }}
            >
              {t('quickLinks')}
            </Typography>
            {renderQuickLinks()}
          </Box>

          <Box
            component='section'
            aria-label={t('didYouKnow')}
          >
            <Typography
              variant='subtitle2'
              sx={{ mb: 2 }}
            >
              {t('didYouKnow')}
            </Typography>
            {tips.length ? (
              <Stack spacing={1.5}>
                {tips.map((tip) => (
                  <ExternalLink
                    key={tip.textKey}
                    url={tip.helpUrl}
                    variant='body2'
                  >
                    {t(tip.textKey)}
                  </ExternalLink>
                ))}
              </Stack>
            ) : (
              <Typography color='text.secondary'>
                {t('advisorQuickLinks.noTips')}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </ContentContainer>
  );
}
